import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Keycloak from 'keycloak-js';
import { KeycloakConfig, resolveKeycloakConfig } from './authConfig';

export type AuthUser = {
  email: string;
  name: string;
};

type AuthContextValue = {
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  user?: AuthUser;
  roles: string[];
  hasAccess: boolean;
  isAdmin: boolean;
  getAccessToken: () => Promise<string>;
  login: () => Promise<void>;
  logout: () => Promise<void>;
};

type AuthProviderProps = React.PropsWithChildren<{
  onAuthenticated?: (returnTo: string) => void;
}>;

type AuthTestProviderProps = React.PropsWithChildren<{
  email?: string;
  name?: string;
  roles?: string[];
  logout?: () => Promise<void> | void;
  [property: string]: unknown;
}>;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const returnToStorageKey = 'distribution-admin-tool.returnTo';

const testAuthValue: AuthContextValue = {
  isLoading: false,
  isAuthenticated: true,
  error: null,
  user: { email: 'testuser123@statsperform.com', name: 'Test User' },
  roles: ['DS:ACCESS'],
  hasAccess: true,
  isAdmin: false,
  getAccessToken: async () => 'access_token',
  login: async () => undefined,
  logout: async () => undefined,
};

let keycloakInstance: Keycloak | undefined;
let initializationPromise: Promise<boolean> | undefined;

const getKeycloak = (config: KeycloakConfig): Keycloak => {
  if (!keycloakInstance) {
    keycloakInstance = new Keycloak(config);
  }
  return keycloakInstance;
};

const getUser = (keycloak: Keycloak, config: KeycloakConfig): AuthUser => {
  const claims = keycloak.idTokenParsed as Record<string, unknown> | undefined;
  const email = claims?.[config.emailClaim];
  const name = claims?.[config.userClaim];

  if (typeof email !== 'string' || typeof name !== 'string') {
    throw new Error(
      'The configured Keycloak email or user identity claim is missing from the ID token.'
    );
  }

  return { email, name };
};

const getClientRoles = (keycloak: Keycloak, clientId: string): string[] => {
  const claims = keycloak.tokenParsed as {
    resource_access?: Record<string, { roles?: unknown }>;
  } | undefined;
  const roles = claims?.resource_access?.[clientId]?.roles;
  return Array.isArray(roles)
    ? roles.filter((role): role is string => typeof role === 'string')
    : [];
};

const isSafeReturnTo = (returnTo: string | null): returnTo is string => (
  Boolean(returnTo)
  && returnTo!.startsWith('/')
  && !returnTo!.startsWith('//')
  && !returnTo!.includes('\\')
);

const getSafeReturnTo = (): string | undefined => {
  const returnTo = window.sessionStorage.getItem(returnToStorageKey);
  window.sessionStorage.removeItem(returnToStorageKey);
  return isSafeReturnTo(returnTo) ? returnTo : undefined;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  onAuthenticated,
}) => {
  const [auth, setAuth] = useState<Omit<AuthContextValue, 'getAccessToken' | 'login' | 'logout'>>({
    isLoading: true,
    isAuthenticated: false,
    error: null,
    roles: [],
    hasAccess: false,
    isAdmin: false,
  });

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      const configResult = resolveKeycloakConfig();
      if (!configResult.config) {
        throw new Error(configResult.error);
      }

      const config = configResult.config;
      const keycloak = getKeycloak(config);
      initializationPromise ??= keycloak.init({
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        redirectUri: new URL('/login', window.location.origin).toString(),
      });

      const isAuthenticated = await initializationPromise;
      const user = isAuthenticated ? getUser(keycloak, config) : undefined;
      const roles = isAuthenticated ? getClientRoles(keycloak, config.clientId) : [];
      const returnTo = isAuthenticated ? getSafeReturnTo() : undefined;

      if (mounted) {
        setAuth({
          isLoading: false,
          isAuthenticated,
          error: null,
          user,
          roles,
          hasAccess: roles.includes('DS:ACCESS'),
          isAdmin: roles.includes('DS:ADMIN'),
        });
        if (returnTo) {
          onAuthenticated?.(returnTo);
        }
      }
    };

    initialize().catch((error: unknown) => {
      if (mounted) {
        setAuth({
          isLoading: false,
          isAuthenticated: false,
          error: error instanceof Error ? error.message : 'Keycloak initialization failed.',
          roles: [],
          hasAccess: false,
          isAdmin: false,
        });
      }
    });

    return () => {
      mounted = false;
    };
  }, [onAuthenticated]);

  const getAccessToken = useCallback(async () => {
    if (!keycloakInstance?.authenticated) {
      throw new Error('Cannot retrieve an access token without an authenticated session.');
    }

    await keycloakInstance.updateToken(30);
    if (!keycloakInstance.token) {
      throw new Error('Keycloak did not provide an access token.');
    }
    return keycloakInstance.token;
  }, []);

  const login = useCallback(async () => {
    if (!keycloakInstance) {
      setAuth((current) => ({ ...current, error: 'Keycloak is not configured.' }));
      return;
    }

    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.sessionStorage.setItem(
      returnToStorageKey,
      isSafeReturnTo(returnTo) ? returnTo : '/',
    );

    try {
      await keycloakInstance.login({
        redirectUri: new URL('/login', window.location.origin).toString(),
      });
    } catch (error) {
      setAuth((current) => ({
        ...current,
        error: error instanceof Error ? error.message : 'Keycloak login failed.',
      }));
    }
  }, []);

  const logout = useCallback(async () => {
    window.sessionStorage.removeItem(returnToStorageKey);
    if (keycloakInstance) {
      await keycloakInstance.logout({
        redirectUri: `${window.location.origin}/logout`,
      });
    }
  }, []);

  const value = useMemo(
    () => ({ ...auth, getAccessToken, login, logout }),
    [auth, getAccessToken, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthTestProvider: React.FC<AuthTestProviderProps> = ({
  children,
  email = testAuthValue.user!.email,
  name = testAuthValue.user!.name,
  roles = testAuthValue.roles,
  logout = testAuthValue.logout,
}) => (
  <AuthContext.Provider value={{
    ...testAuthValue,
    user: { email, name },
    roles,
    hasAccess: roles.includes('DS:ACCESS'),
    isAdmin: roles.includes('DS:ADMIN'),
    logout: async () => logout(),
  }}>
    {children}
  </AuthContext.Provider>
);

export const useAuth = (): AuthContextValue => {
  const auth = useContext(AuthContext);
  if (!auth) {
    throw new Error('useAuth must be used within an AuthProvider.');
  }
  return auth;
};